<script setup lang="ts">
import { type Category, CategoryType, type EventDetails } from "mws-ts-v2-sdk";

import { useLocalizedDateTime } from "~/composables/useLocalizedDateTime";

const { event } = defineProps<{ event: EventDetails }>();

const isImageLoaded = ref(false);

const endDate = computed(() => event.endDate);
const { localizedDateTime: localizedEndDate } = useLocalizedDateTime(
  endDate,
  "dateTime",
);

const biddingEnded = computed(() => {
  if (!event) return false;

  return event.endDate < new Date();
});

const eventStarted = computed(() => {
  if (!event) return false;

  return event.startDate <= new Date();
});

const { featuredTeam } = useEventDetails(() => event);

const featureToggles = useFeatureToggles();
const platformStore = usePlatformStore();

const sortOrder: (CategoryType | undefined)[] = [
  CategoryType.Team,
  CategoryType.Other,
  CategoryType.League,
  CategoryType.Country,
  CategoryType.Sport,
  CategoryType.Gender,
  undefined,
];

const customSort = (a: Category, b: Category): number => {
  const typeA = sortOrder.indexOf(a.externalReference?.type);
  const typeB = sortOrder.indexOf(b.externalReference?.type);

  // Sort by type index
  if (typeA !== typeB) {
    return typeA - typeB;
  }

  // Sort alphabetically by name
  return a.name.localeCompare(b.name);
};

const { isMobile } = useScreenSize();
const bannerPreset = computed(() => {
  if (isMobile.value) return "categoryBannerMobile";

  return "categoryBannerDesktop";
});

const leagueCategoryName = computed(() => {
  const leagueCategory = event.categories.find(
    (category) => category.externalReference?.type === CategoryType.League,
  );

  return leagueCategory?.name || "";
});

const leagueCategoryUrl = computed(() => {
  const leagueCategory = event.categories.find(
    (category) => category.externalReference?.type === CategoryType.League,
  );

  if (leagueCategory?.slug) {
    return `/category/${leagueCategory.slug}`;
  }

  return null;
});

const featuredPageLinks = computed(() => {
  const categories = [...event.categories];

  return categories
    .filter(
      (c) =>
        c.externalReference?.type !== CategoryType.Gender &&
        c.externalReference?.type !== CategoryType.Sport,
    ) // we dont want to send users to these pages due to its performance issues related to the database load. @todo remove this when the performance issues are resolved
    .sort(customSort)
    .map((c) => {
      return {
        label: c.name || "Category",
        route: `/category/${c.slug}`,
      };
    });
});

const featuredTeamCategoryRoute = computed(() => {
  const featuredTeamsCategory = event.categories.find(
    (c) =>
      c.externalReference?.externalId === featuredTeam.value?.participantId,
  );

  if (featuredTeamsCategory) {
    return `/category/${featuredTeamsCategory.slug}`;
  }

  return null;
});

const bannerImageUrl = computed(() => getBannerUrlForEvent(event));

const eventOverlayAHex = computed(() => {
  const defaultAlphaValue = "66";
  const hexCode =
    platformStore.getPlatformConfiguration?.styling.theme["overlay-1"];

  return hexCode.startsWith("#")
    ? `${defaultAlphaValue}${hexCode.slice(1)}`
    : `${defaultAlphaValue}${hexCode}`;
});

const localePath = useLocalePath();

const { leaguePressed } = useAnalytics();

const onLeagueCategoryClicked = () =>
  leaguePressed({
    event_name: event.name,
    league_name: leagueCategoryName.value,
    origin: "event",
  });

const { setTeamName } = useTeamContext();
watchEffect(() => {
  if (featuredTeam.value) {
    setTeamName(featuredTeam.value.name);
  }
});
</script>

<template>
  <div class="event-hero-block">
    <div class="grid grid-cols-12">
      <div class="event-hero-block__header site-viewport-container">
        <EventName
          :event="event"
          class="text-2xl md:text-4xl uppercase"
        >
          <template #homeTeam="{ teamName, featured }">
            <h1
              v-if="featured && featuredTeamCategoryRoute"
              class="font-bold"
              data-test="home-team"
            >
              <NuxtLink :to="localePath(featuredTeamCategoryRoute)">
                {{ teamName }}
              </NuxtLink>
            </h1>
            <h2
              v-else
              data-test="home-team"
            >
              {{ teamName }}
            </h2>
          </template>
          <template #awayTeam="{ teamName, featured }">
            <h1
              v-if="featured && featuredTeamCategoryRoute"
              class="font-bold"
              data-test="away-team"
            >
              <NuxtLink :to="localePath(featuredTeamCategoryRoute)">
                {{ teamName }}
              </NuxtLink>
            </h1>
            <h2
              v-else
              data-test="away-team"
            >
              {{ teamName }}
            </h2>
          </template>
        </EventName>
        <div class="event-hero-block__header--logo">
          <NuxtImg
            provider="imgix"
            loading="lazy"
            preset="logoLarge"
            :src="event.logoUrl || logoFallbackPath"
            alt="logo"
            @error="setBrokenMediaFallback"
          />
        </div>
      </div>

      <div class="event-hero-block__content site-viewport-container">
        <div
          class="decoration-gray-300 underline decoration-dashed underline-offset-4 text-xs md:pt-2 uppercase"
        >
          <NuxtLink
            v-if="leagueCategoryUrl"
            :to="localePath(leagueCategoryUrl)"
            data-cy="category-league-link"
            @click="onLeagueCategoryClicked"
          >
            {{ leagueCategoryName }}
          </NuxtLink>
        </div>
        <div class="event-hero-block__content--info">
          <div class="flex flex-row gap-8">
            <div>
              <p
                class="flex flex-col text-2xs leading-5 uppercase text-platform-text-3"
              >
                {{ !biddingEnded ? $t("event.timeLeft") : $t("event.closed") }}
              </p>
              <BaseCountdownTimer
                v-if="eventStarted && !biddingEnded"
                class="event-hero-block__content--info-countdown"
                :use-color-coding="true"
                :end-date="event.endDate"
                :finished="!!biddingEnded"
              />
              <div
                v-if="!eventStarted"
                class="font-semibold"
                data-test="coming-soon-text"
              >
                {{ $t("EventHeroCharity.comingSoon") }}
              </div>
              <div
                v-if="biddingEnded"
                class="event-hero-block__content--info-ended"
              >
                {{ localizedEndDate }}
              </div>
            </div>
          </div>
          <EventFollowButton
            v-if="featureToggles.customerPreferences && !biddingEnded"
            :id="event.id"
            variant="border"
            :name="event.name"
            :slug="event.slug"
            :is-followed-by-user="event.isFollowedByUser ?? false"
          />
        </div>
      </div>
    </div>
    <div>
      <div class="hide-mobile dropdown-container">
        <div class="site-viewport-container">
          <BaseDropdown
            v-if="featuredPageLinks.length > 1"
            :title="$t('event.viewAllEventsWithoutName')"
            :links="featuredPageLinks"
          />
          <NuxtLink
            v-else-if="featuredPageLinks[0]"
            class="base-button interaction-dark w-fit flex items-center"
            :to="localePath(featuredPageLinks[0].route)"
            data-test="view-all-events-link"
          >
            {{
              $t(
                featuredPageLinks[0].label
                  ? "event.viewAllEventsWithName"
                  : "event.viewAllEventsWithoutName",
                { teamName: featuredPageLinks[0].label },
              )
            }}
            <BaseIcon
              name="chevronRight"
              :size="20"
            />
          </NuxtLink>
        </div>
      </div>

      <div
        v-if="!isImageLoaded"
        class="event-hero-block__background-image w-full overflow-hidden"
      >
        <BaseSkeletonLoader
          :width="4200"
          :height="2960"
        />
      </div>
      <!-- Hydration/Cache issue https://github.com/nuxt/image/issues/682#issuecomment-1570884487 -->
      <ClientOnly>
        <NuxtImg
          v-show="isImageLoaded"
          provider="imgix"
          :alt="`${event.name} header image`"
          sizes="sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw 3xl:100vw"
          :preset="bannerPreset"
          :src="bannerImageUrl"
          class="w-full"
          :modifiers="{ blendMode: 'normal', blendColor: eventOverlayAHex }"
          @load="isImageLoaded = true"
        />
      </ClientOnly>
      <div class="event-hero-block__view-all-button-mobile">
        <NuxtImg
          provider="imgix"
          loading="lazy"
          preset="logoLarge"
          :src="event.logoUrl || logoFallbackPath"
          alt="logo"
          class="event-hero-block__view-all-button-mobile-image"
          @error="setBrokenMediaFallback"
        />
        <NuxtLink
          v-if="featuredPageLinks[0]"
          class="justify-between flex w-full items-center uppercase"
          :to="localePath(featuredPageLinks[0].route)"
          data-test="view-all-events-link"
        >
          {{
            $t(
              featuredPageLinks[0].label
                ? "event.viewAllEventsWithName"
                : "event.viewAllEventsWithoutName",
              { teamName: featuredPageLinks[0].label },
            )
          }}
          <BaseIcon
            name="chevronRight"
            :size="20"
          />
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.hide-mobile {
  @apply hidden md:grid;
}

.dropdown-container {
  @apply grid-cols-12 absolute w-full mt-3;
}
.event-hero-block {
  @apply flex flex-col mt-7 gap-3;
  @apply text-black;

  &__header {
    @apply flex flex-row items-center justify-between md:justify-start;
    @apply gap-0 pb-4 md:gap-8 md:pb-0 lg:gap-16;

    &--logo {
      @apply flex items-center aspect-square h-12 object-contain;
    }
  }

  &__content {
    @apply flex flex-col justify-between gap-3 md:flex-row;

    &--info {
      @apply flex flex-row gap-8 items-end justify-between md:justify-normal md:pt-4;

      &-countdown {
        @apply text-sm;
      }

      &-ended {
        @apply text-sm font-semibold p-0;
      }
    }
  }
  &__view-all-button-mobile {
    @apply flex flex-row gap-3 items-center md:hidden;
    @apply p-3 border-b-[1px];
    @apply text-sm font-semibold;

    &-image {
      @apply aspect-square h-6 object-contain;
    }
  }
  &__background-image {
    @apply w-full object-cover aspect-[2100/1480] md:aspect-[2100/800];
  }
}
</style>
